import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

// API 基础地址（去掉末尾的 /api，因为请求路径已经包含 /api）
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/api$/, '');

/**
 * 移动端检测并跳转微信小程序
 * 统一使用 URL Scheme 方案（微信内和外部浏览器都适用）
 * 注意：使用开放标签需要已认证的服务号，URL Scheme 只需要小程序后台开通即可
 */
const redirectToMiniProgram = async (): Promise<boolean> => {
  const ua = navigator.userAgent.toLowerCase();
  
  // 检测是否为移动端
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  
  if (!isMobile) {
    return false; // PC端不跳转，继续加载H5
  }
  
  // 检查URL参数，允许通过 ?h5=1 强制使用H5版本
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('h5') === '1') {
    return false;
  }
  
  // 显示加载中的引导页
  document.body.innerHTML = `
    <div id="miniprogram-redirect" style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      text-align: center;
      padding: 20px;
    ">
      <div style="font-size: 48px; margin-bottom: 20px;">🏠</div>
      <h1 style="font-size: 24px; margin-bottom: 10px;">如目云</h1>
      <p style="font-size: 14px; opacity: 0.9; margin-bottom: 30px;">专业装修服务平台</p>
      <div style="font-size: 14px; opacity: 0.8;">正在加载...</div>
    </div>
  `;
  
  // 用于存储错误信息
  let lastError = '';
  
  // 使用 URL Scheme 唤起小程序
  try {
    const schemeResponse = await fetch(`${API_BASE}/api/wechat/jssdk/miniprogram-scheme`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        path: '/pages/index/index',
        query: ''
      })
    });
    const schemeData = await schemeResponse.json();
    
    // 调试信息
    console.log('URL Scheme 响应:', schemeData);
    lastError = JSON.stringify(schemeData);
    
    if (schemeData.scheme) {
      // 显示引导页面
      document.body.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-align: center;
          padding: 20px;
        ">
          <div style="font-size: 48px; margin-bottom: 20px;">🏠</div>
          <h1 style="font-size: 24px; margin-bottom: 10px;">如目云</h1>
          <p style="font-size: 14px; opacity: 0.9; margin-bottom: 30px;">专业装修服务平台</p>
          <button id="open-miniprogram" style="
            width: 200px;
            height: 50px;
            background: white;
            color: #667eea;
            border: none;
            border-radius: 25px;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
          ">打开小程序</button>
          <p style="font-size: 12px; opacity: 0.7; margin-top: 20px;">
            <a href="?h5=1" style="color: white;">继续使用网页版</a>
          </p>
        </div>
      `;
      
      // 绑定点击事件
      const btn = document.getElementById('open-miniprogram');
      if (btn) {
        btn.addEventListener('click', () => {
          window.location.href = schemeData.scheme;
          // 3秒后检查是否还在当前页面
          setTimeout(() => {
            if (confirm('未能打开微信小程序，是否继续使用网页版？')) {
              window.location.href = '?h5=1';
            }
          }, 3000);
        });
      }
      
      return true;
    }
    
  } catch (error: any) {
    console.error('获取 URL Scheme 失败:', error);
    lastError = error?.message || String(error);
  }
  
  // 如果获取 scheme 失败，显示备用页面（带调试信息）
  document.body.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      text-align: center;
      padding: 20px;
    ">
      <div style="font-size: 48px; margin-bottom: 20px;">🏠</div>
      <h1 style="font-size: 24px; margin-bottom: 10px;">如目云</h1>
      <p style="font-size: 14px; opacity: 0.9; margin-bottom: 30px;">专业装修服务平台</p>
      <p style="font-size: 14px; margin-bottom: 20px;">请打开微信搜索"如目云"小程序</p>
      <a href="?h5=1" style="
        display: inline-block;
        padding: 12px 30px;
        background: white;
        color: #667eea;
        border-radius: 25px;
        text-decoration: none;
        font-weight: bold;
      ">继续使用网页版</a>
      <p style="font-size: 10px; opacity: 0.6; margin-top: 30px; word-break: break-all; max-width: 300px;">
        调试信息:<br>
        API: ${API_BASE || '(空)'}<br>
        响应: ${lastError}
      </p>
    </div>
  `;
  return true;
};

/**
 * 检查是否为浏览器扩展相关的错误
 */
const isBrowserExtensionError = (error: any, eventMessage?: string): boolean => {
  if (!error && !eventMessage) return false;
  
  const errorMessage = error ? (error.message || error.reason?.message || String(error)).toLowerCase() : '';
  const evtMessage = eventMessage ? String(eventMessage).toLowerCase() : '';
  const combinedMessage = `${errorMessage} ${evtMessage}`;
  
  return (
    combinedMessage.includes('message channel closed') ||
    combinedMessage.includes('a listener indicated an asynchronous response') ||
    combinedMessage.includes('third-party cookies') ||
    combinedMessage.includes('chrome-extension://') ||
    combinedMessage.includes('extension context invalidated') ||
    combinedMessage.includes('chrome.runtime.sendmessage') ||
    combinedMessage.includes('redirecting to miniprogram')
  );
};

// 全局错误处理 - 同步错误
window.addEventListener('error', (event) => {
  if (isBrowserExtensionError(event.error, event.message)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    console.debug('[忽略] 浏览器扩展错误:', event.message || event.error?.message);
    return;
  }
  
  if (event.error) {
    console.error('应用错误:', event.error);
  }
}, true);

// 全局错误处理 - Promise错误
window.addEventListener('unhandledrejection', (event) => {
  const reasonMessage = event.reason?.message || String(event.reason || '');
  
  if (isBrowserExtensionError(event.reason, reasonMessage)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    console.debug('[忽略] 浏览器扩展Promise错误:', event.reason?.message || event.reason);
    return;
  }
  
  if (event.reason) {
    console.error('未处理的Promise错误:', event.reason);
  }
}, true);

/**
 * 加载 React 应用
 */
function loadReactApp() {
  const isDevelopment = import.meta.env.DEV;
  const useStrictMode = import.meta.env.VITE_USE_STRICT_MODE === 'true' || (!isDevelopment);

  console.log(`🔧 环境信息: ${isDevelopment ? '开发环境' : '生产环境'}`);
  console.log(`🔧 StrictMode: ${useStrictMode ? '启用' : '禁用'}`);
  console.log('🚀 [main.tsx] 开始初始化应用...');

  if (typeof React === 'undefined') {
    console.error('❌ React 未加载');
    throw new Error('React is not loaded.');
  }
  console.log('✅ React 已加载');

  if (typeof createRoot === 'undefined') {
    console.error('❌ createRoot 未加载');
    throw new Error('createRoot is not loaded.');
  }
  console.log('✅ createRoot 已加载');

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('❌ root 元素未找到');
    throw new Error('Root element not found');
  }
  console.log('✅ root 元素已找到');

  console.log('🎨 [main.tsx] 开始渲染应用...');
  try {
    createRoot(rootElement).render(
      useStrictMode ? (
        <StrictMode>
          <App />
        </StrictMode>
      ) : (
        <App />
      )
    );
    console.log('✅ [main.tsx] 应用渲染完成');
  } catch (error) {
    console.error('❌ [main.tsx] 应用渲染失败:', error);
    throw error;
  }
}

// 执行移动端跳转检测（异步）
(async () => {
  try {
    const redirected = await redirectToMiniProgram();
    if (redirected) {
      return; // 已显示小程序引导页
    }
  } catch (error) {
    console.error('移动端跳转检测失败:', error);
  }
  
  // 继续加载 React 应用
  loadReactApp();
})();
